import { FormattedMessage, defineMessages } from 'react-intl';
import { animated, useSpring, useSprings } from 'react-spring';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL, LabelS } from '@sorare/core/src/atoms/typography';
import { ExtraMessageWithBlurredBg } from '@sorare/core/src/components/onboarding/ExtraMessageWithBlurredBg';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { SUPPLY, scarcityNames } from '@sorare/core/src/lib/cards';
import { glossary } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import blueprintLimited from '@sorare/gameplay/src/assets/revealable-cards/blueprint-limited.png';
import blueprintRare from '@sorare/gameplay/src/assets/revealable-cards/blueprint-rare.png';
import blueprintSuperRare from '@sorare/gameplay/src/assets/revealable-cards/blueprint-super_rare.png';
import blueprintUnique from '@sorare/gameplay/src/assets/revealable-cards/blueprint-unique.png';

const rarityMessages: {
  [key: string]: { id: string; defaultMessage: string };
} = defineMessages({
  perSeason: {
    id: 'S3Onboarding.rarity.number',
    defaultMessage: '{number} per season',
  },
  unlimited: {
    id: 'S3Onboarding.rarity.unlimited',
    defaultMessage: 'Unlimited',
  },
});

const RarityNumber = styled(LabelS)`
  text-wrap: nowrap;
  width: min-content;
  padding: 2px var(--unit);
  border-radius: var(--unit);
`;

const CommonWrapper = styled(Vertical).attrs({ gap: 0.5 })`
  position: absolute;
  align-items: center;

  color: var(--c-white);

  ${RarityNumber} {
    background: rgba(255, 255, 255, 0.07);
  }
`;

const BlockChainScarcitiesWrapper = styled(Vertical).attrs({ gap: 0.5 })`
  position: absolute;
`;

const RarityWrapper = styled(Horizontal).attrs({ gap: 0.5 })`
  &.Limited {
    color: #efcd1d;
    ${RarityNumber} {
      background: #f0c95629;
    }
  }
  &.Rare {
    color: #f33a26;
    ${RarityNumber} {
      background: #f5675729;
    }
  }
  &.SuperRare {
    color: #0196fe;
    ${RarityNumber} {
      background: #294bff29;
    }
  }
  &.Unique {
    color: #e9cdf6;
    ${RarityNumber} {
      background: linear-gradient(
        228.5deg,
        rgba(173, 0, 255, 0.4) -13.01%,
        rgba(104, 0, 153, 0.4) 62%
      );
    }
  }
`;

const StyledExtraMessage = styled(ExtraMessageWithBlurredBg)`
  left: var(--unit);
  right: var(--unit);
  @media ${tabletAndAbove} {
    max-width: 340px;
  }
`;

const StyledButton = styled(Button)`
  position: absolute;
  left: var(--unit);
  right: var(--unit);
  bottom: var(--quadruple-unit);

  @media ${tabletAndAbove} {
    max-width: 340px;
  }
`;

type Props = {
  left: number;
  top: number;
  right: number;
  bottom: number;
  onClose?: () => void;
};

export const GalleryCardExtra = ({
  top,
  bottom,
  right,
  left,
  onClose,
}: Props) => {
  const parentWidth = right - left;
  const isTabletAndAbove = useIsTabletAndAbove();

  const blockchainScarcities = [
    {
      src: blueprintLimited,
      number: SUPPLY.limited,
      key: scarcityNames.limited,
      rarityMessagesKey: 'perSeason',
    },
    {
      src: blueprintRare,
      number: SUPPLY.rare,
      key: scarcityNames.rare,
      rarityMessagesKey: 'perSeason',
    },
    {
      src: blueprintSuperRare,
      number: SUPPLY.super_rare,
      key: scarcityNames.superRare,
      rarityMessagesKey: 'perSeason',
    },
    {
      src: blueprintUnique,
      number: SUPPLY.unique,
      key: scarcityNames.unique,
      rarityMessagesKey: 'perSeason',
    },
  ];

  const springs = useSprings(
    blockchainScarcities.length,
    blockchainScarcities.map((_, index) => ({
      from: { opacity: 0 },
      to: { opacity: 1 },
      config: { mass: 1, tension: 280, friction: 20 },
      delay: index * 1000,
    }))
  );

  const lastStepSpring = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    config: { mass: 1, tension: 280, friction: 20 },
    delay: blockchainScarcities.length * 1000, // Delay based on the number of scarcities
  });

  return (
    <>
      <CommonWrapper
        style={{
          left,
          right,
          width: parentWidth,
          top: `calc(${bottom}px + var(--unit))`,
        }}
      >
        <LabelL bold>{scarcityNames.common}</LabelL>
        <RarityNumber bold>
          <FormattedMessage {...rarityMessages.unlimited} />
        </RarityNumber>
      </CommonWrapper>
      <BlockChainScarcitiesWrapper
        style={{
          top,
          left: `calc(${left}px + ${parentWidth}px + var(--unit))`,
        }}
      >
        {springs.map((props, index) => {
          const { src, number, key, rarityMessagesKey } =
            blockchainScarcities[index];
          return (
            <animated.div key={key} style={props}>
              <RarityWrapper className={key.replace(' ', '')}>
                <img width={43} src={src} alt="" />
                <Vertical gap={0.5}>
                  <LabelL bold>{key}</LabelL>
                  <RarityNumber bold>
                    <FormattedMessage
                      {...rarityMessages[rarityMessagesKey]}
                      values={{ number }}
                    />
                  </RarityNumber>
                </Vertical>
              </RarityWrapper>
            </animated.div>
          );
        })}
      </BlockChainScarcitiesWrapper>
      <animated.div style={lastStepSpring}>
        <StyledExtraMessage
          style={{
            left: isTabletAndAbove
              ? `calc(${left}px - var(--double-unit))`
              : '',
            top: `calc(${top}px - 92px - var(--triple-unit))`,
          }}
        >
          <LabelL>
            <FormattedMessage
              id="NBA.managerProfile.GalleryOnboarding.Scarcities"
              defaultMessage="Each player has 5 card rarities. You can buy, sell, and trade Pro Cards on the Market"
            />
          </LabelL>
        </StyledExtraMessage>
        <StyledButton
          size="large"
          color="secondary"
          onClick={onClose}
          fullWidth={isTabletAndAbove}
          style={{
            left: isTabletAndAbove
              ? `calc(${left}px - var(--double-unit))`
              : '',
            top: isTabletAndAbove
              ? `calc(${bottom}px + 42px + var(--unit) * 6)`
              : '',
          }}
        >
          <FormattedMessage {...glossary.next} />
        </StyledButton>
      </animated.div>
    </>
  );
};
